import * as Icons from "heroicons-react";
import { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  useAddUserMutation,
  useUserCountQuery,
  useUsersQuery,
} from "../../services/userApi";
import { useWarehousesQuery } from "../../services/warehouseApi";
import WareHouseDWUser from "../Common/CustomSelect/WareHouseDWUser";
import Header from "../Common/Header/Header";
import PackageModal from "../Common/Modal/PackageModal";
import SideBar from "../Common/SideBar/SideBar";
import { signInUser } from "../Utility/Auth";
import { notify } from "../Utility/Notify";

const AddUser = () => {
  const auth = signInUser();
  const aamarId = auth?.aamarId;
  const lang = useSelector((state: any) => state.languageReducer);
  const navigate = useNavigate();

  // Queries
  const { data: userData, refetch: refetchUserCount } = useUserCountQuery(
    { aamarId },
    { skip: !aamarId }
  );
  const { data: usersList } = useUsersQuery(
    { warehouse: "allWh", aamarId },
    { skip: !aamarId }
  );
  const { data: warehousesList } = useWarehousesQuery(
    { aamarId },
    { skip: !aamarId }
  );
  const [createNewUser, { isLoading: isSubmitting }] = useAddUserMutation();

  // Warehouse Resolution
  const rawAuthWh = auth?.warehouse;
  const authWhId =
    typeof rawAuthWh === "string"
      ? rawAuthWh
      : rawAuthWh?._id || rawAuthWh?.id || "";
  const authWhName =
    typeof rawAuthWh === "object" && rawAuthWh?.name ? rawAuthWh.name : "";

  const firstWh =
    Array.isArray(warehousesList) && warehousesList.length > 0
      ? warehousesList[0]
      : null;
  const initialWhId = authWhId || firstWh?._id || "";
  const initialWhName = authWhName || firstWh?.name || "Main Warehouse";

  const [wh, setWh] = useState(initialWhId);
  const [whName, setWhName] = useState(initialWhName);
  const [modalVisible, setModalVisible] = useState(false);
  const closeModal = () => setModalVisible(false);

  // Sync warehouse if initial query resolves later
  useEffect(() => {
    if (!wh && initialWhId) {
      setWh(initialWhId);
    }
    if ((!whName || whName === "Warehouse") && initialWhName) {
      setWhName(initialWhName);
    }
  }, [initialWhId, initialWhName, wh, whName]);

  // Plan detection and limits
  const pkg = auth?.package;
  const planLimited = (pkg?.planLimited || pkg?.plan || "").toLowerCase().trim();
  const pId = String(
    pkg?.pId || auth?.storeSettings?.pId || ""
  ).toLowerCase().trim();

  const isPremium =
    planLimited === "premium" ||
    pId.includes("premium") ||
    pId.endsWith("-03") ||
    pId.endsWith("-04") ||
    pId === "aid-pos-03";

  const isStartup =
    planLimited === "startup" ||
    pId.includes("startup") ||
    pId.endsWith("-01") ||
    pId === "aid-pos-01";

  // Calculate plan limit: Startup = 1, Basic = 3, Premium = 10
  let planLimit = 3;
  if (
    pkg?.userLimited &&
    typeof pkg.userLimited === "number" &&
    pkg.userLimited > 0
  ) {
    planLimit = pkg.userLimited;
  } else if (isPremium) {
    planLimit = 10;
  } else if (isStartup) {
    planLimit = 1;
  } else {
    planLimit = 3; // Basic plan limit
  }

  const planDisplayName = isPremium ? "Premium" : isStartup ? "Startup" : "Basic";
  const currentUserCount =
    typeof userData === "number"
      ? userData
      : Array.isArray(usersList)
      ? usersList.length
      : 0;
  const isLimitReached = currentUserCount >= planLimit;

  // Role management & limitations
  const creatorType = auth?.type || "admin";
  const hasExistingOwner =
    Array.isArray(usersList) && usersList.some((u: any) => u?.type === "owner");

  const getAvailableRoles = () => {
    const roles: { value: string; label: string; disabled: boolean }[] = [];

    // Owner role: Only if creator is owner/admin and no owner exists in store yet
    if (!hasExistingOwner && (creatorType === "owner" || creatorType === "admin")) {
      roles.push({ value: "owner", label: "Owner", disabled: false });
    }

    // Admin role: Allowed if creator is owner or admin
    if (creatorType === "owner" || creatorType === "admin") {
      roles.push({ value: "admin", label: "Admin", disabled: false });
    }

    // Manager role: Allowed if creator is owner or admin
    if (creatorType === "owner" || creatorType === "admin") {
      roles.push({ value: "manager", label: "Manager", disabled: false });
    }

    // POS role: Always available for owner, admin, or manager
    roles.push({
      value: "POS",
      label: "POS (Cashier / Biller)",
      disabled: false,
    });

    // Premium-only roles: ecom, supervisor, accounts
    if (creatorType === "owner" || creatorType === "admin") {
      roles.push({
        value: "ecom",
        label: isPremium ? "Ecom Manager" : "Ecom Manager (Premium Only)",
        disabled: !isPremium,
      });
      roles.push({
        value: "supervisor",
        label: isPremium ? "Supervisor" : "Supervisor (Premium Only)",
        disabled: !isPremium,
      });
      roles.push({
        value: "accounts",
        label: isPremium ? "Accounts" : "Accounts (Premium Only)",
        disabled: !isPremium,
      });
    }

    return roles;
  };

  const { register, handleSubmit, reset } = useForm<any>({
    defaultValues: {
      name: "",
      username: "",
      email: "",
      phone: "",
      password: "",
      type: "POS",
      status: "active",
    },
  });

  const handleOnchangeWareHouse = (e: any) => {
    if (e && e.option) {
      setWh(e.option);
      if (e.label) setWhName(e.label);
    }
  };

  const handleAddUser = async (formData: any) => {
    if (isLimitReached) {
      setModalVisible(true);
      notify(
        `User limit reached (${currentUserCount}/${planLimit}) for ${planDisplayName} plan. Please upgrade to add more users.`,
        "error"
      );
      return;
    }

    const resolvedWarehouse =
      wh ||
      initialWhId ||
      (Array.isArray(warehousesList) && warehousesList[0]?._id) ||
      "";

    if (!resolvedWarehouse || resolvedWarehouse === "no-warehouse") {
      notify("Please Select a Warehouse", "error");
      return;
    }

    if (!formData.name || !formData.name.trim()) {
      notify("Please enter a name", "error");
      return;
    }

    if (!formData.username || !formData.username.trim()) {
      notify("Please enter a username", "error");
      return;
    }

    if (!formData.password || !formData.password.trim()) {
      notify("Please enter a password", "error");
      return;
    }

    // Block non-premium from selecting premium roles
    const selectedType = formData.type || "POS";
    const premiumRoles = ["ecom", "supervisor", "accounts"];
    if (premiumRoles.includes(selectedType) && !isPremium) {
      notify(`Role "${selectedType}" requires a Premium Plan.`, "error");
      setModalVisible(true);
      return;
    }

    const payload = {
      name: formData.name.trim(),
      username: formData.username.trim().toLowerCase(),
      email: formData.email ? formData.email.trim().toLowerCase() : "",
      phone: formData.phone ? formData.phone.trim() : "",
      password: formData.password.trim(),
      type: selectedType,
      status: formData.status || "active",
      warehouse: resolvedWarehouse,
      aamarId: auth?.aamarId,
    };

    try {
      const response: any = await createNewUser(payload as any);
      if (response?.error) {
        const errorData = response.error?.data;
        const errMsg =
          errorData?.message ||
          errorData?.error?.message ||
          "Failed to add user";
        notify(errMsg, "error");
      } else {
        notify(
          response?.data?.message || "User added successfully!",
          "success"
        );
        reset({
          name: "",
          email: "",
          username: "",
          phone: "",
          password: "",
          status: "active",
          type: "POS",
        });
        if (refetchUserCount) refetchUserCount();
        return navigate("/user");
      }
    } catch (err: any) {
      notify(err?.message || "An unexpected error occurred", "error");
    }
  };

  return (
    <div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-2">
            <SideBar />
          </div>
          <div className="col-md-10">
            <Header title={lang?.createNewUser || "Create New User"} />
            <div className="row mt-4">
              <div className="col-md-6 offset-md-3">
                <div className="sticky-top">
                  {/* Plan & Usage Summary Card */}
                  <div className="alert alert-secondary d-flex justify-content-between align-items-center mb-3 py-2">
                    <div>
                      <strong>Plan: </strong>
                      <span
                        className={`badge ${
                          isPremium ? "bg-primary" : "bg-info text-dark"
                        } text-uppercase me-2`}
                      >
                        {planDisplayName}
                      </span>
                      <span>
                        Users:{" "}
                        <strong>
                          {currentUserCount} / {planLimit}
                        </strong>
                      </span>
                    </div>
                    {isLimitReached ? (
                      <span className="badge bg-danger">Limit Reached</span>
                    ) : (
                      <span className="badge bg-success">
                        {planLimit - currentUserCount} Slot(s) Left
                      </span>
                    )}
                  </div>

                  {/* Warning banner when limit reached */}
                  {isLimitReached && (
                    <Alert
                      variant="warning"
                      className="d-flex justify-content-between align-items-center mb-3"
                    >
                      <div>
                        <Icons.ExclamationOutline size={20} className="me-2" />
                        You have reached the limit of <strong>{planLimit} user(s)</strong> for your{" "}
                        <strong>{planDisplayName} Plan</strong>.
                      </div>
                      <Button
                        variant="dark"
                        size="sm"
                        onClick={() => setModalVisible(true)}
                      >
                        Upgrade
                      </Button>
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit(handleAddUser)}>
                    <Form.Group className="mb-3" controlId="Name">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        {...register("name", { required: true })}
                        type="text"
                        name="name"
                        placeholder="Full Name"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="UserName">
                      <Form.Label>User Name</Form.Label>
                      <Form.Control
                        {...register("username", { required: true })}
                        type="text"
                        name="username"
                        placeholder="User Name"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="Email">
                      <Form.Label>Email address</Form.Label>
                      <Form.Control
                        {...register("email")}
                        type="email"
                        name="email"
                        placeholder="Enter email"
                      />
                      <Form.Text className="text-muted">
                        We'll never share your email with anyone else.
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicPhone">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        {...register("phone")}
                        type="text"
                        name="phone"
                        placeholder="Phone Number"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicPassword">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        {...register("password", { required: true })}
                        type="password"
                        name="password"
                        placeholder="Password"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicWarehouse">
                      <Form.Label>Warehouse</Form.Label>
                      {creatorType === "admin" || creatorType === "owner" ? (
                        <WareHouseDWUser
                          name="warehouse"
                          handleOnChange={handleOnchangeWareHouse}
                          warehouse={wh || initialWhId}
                        />
                      ) : (
                        <Form.Control
                          type="text"
                          disabled
                          value={whName || initialWhName || "Assigned Warehouse"}
                        />
                      )}
                    </Form.Group>

                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group className="mb-3" controlId="formBasicType">
                          <Form.Label>User Type (Role)</Form.Label>
                          <Form.Select {...register("type")}>
                            {getAvailableRoles().map((role) => (
                              <option
                                key={role.value}
                                value={role.value}
                                disabled={role.disabled}
                              >
                                {role.label}
                              </option>
                            ))}
                          </Form.Select>
                          {!isPremium && (
                            <Form.Text className="text-muted">
                              Basic Plan supports POS, Manager &amp; Admin roles.
                            </Form.Text>
                          )}
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group className="mb-3" controlId="formBasicStatus">
                          <Form.Label>Status</Form.Label>
                          <Form.Select {...register("status")}>
                            <option value="active">Active</option>
                            <option value="suspend">Suspend</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </div>

                    <div className="d-flex justify-content-center gap-2 mt-3">
                      <Link to="/user" className="btn btn-outline-dark">
                        <Icons.X size={20} /> Cancel
                      </Link>

                      <Button
                        variant="dark"
                        type="submit"
                        disabled={isLimitReached || isSubmitting}
                      >
                        <Icons.PlusOutline size={20} />{" "}
                        {isSubmitting ? "Adding User..." : "Add User"}
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Package Info Modal */}
        <PackageModal show={modalVisible} handleClose={closeModal} />
      </div>
    </div>
  );
};

export default AddUser;
